import React, { ChangeEvent, useEffect, useRef, useState } from "react";

import { FormHelperText, Grid, LinearProgress, Typography } from "@mui/material";
import uploadBtn from "app/containers/Patients/assets/Upload.png";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import {
  AUDIO_PRESETS,
  uploadAudioPresetThumbnail,
} from "../../assets/audio-presets";
import { UploadedResource } from "../../types";
import { uploadFileToS3 } from "./actions";
import { useStyles } from "./styles";

const ACCEPT_ATTR = "video/*,audio/*,application/pdf";

const detectByExtension = (
  name: string
): "video" | "audio" | "pdf" | undefined => {
  const lower = name.toLowerCase();
  const ext = lower.slice(lower.lastIndexOf(".") + 1);
  const videoExts = [
    "mp4",
    "mov",
    "m4v",
    "webm",
    "ogv",
    "ogg",
    "mkv",
    "avi",
    "wmv",
    "flv",
    "3gp",
    "3g2",
    "mpeg",
    "mpg",
    "qt",
    "hevc",
  ];
  const audioExts = [
    "mp3",
    "wav",
    "wave",
    "m4a",
    "aac",
    "flac",
    "ogg",
    "oga",
    "opus",
    "weba",
    "wma",
    "amr",
    "aiff",
    "aif",
    "caf",
  ];
  if (videoExts.includes(ext)) return "video";
  if (audioExts.includes(ext)) return "audio";
  if (ext === "pdf") return "pdf";
  return undefined;
};

type Props = {
  onUpload({
    resourcePath,
    resourceName,
    resourceSize,
    resourceType,
    url,
  }: UploadedResource): void;
};

export const UploadField = ({ onUpload }: Props) => {
  const [progress, setProgress] = useState(0);
  const styles = useStyles();
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const docRef = useRef<HTMLInputElement | null>(null);

  function docClick() {
    if (docRef.current) docRef.current.click();
  }

  const detectType = (file: File): "video" | "audio" | "pdf" | undefined => {
    const mime = (file.type || "").toLowerCase();
    if (mime === "application/pdf") return "pdf";
    if (mime.startsWith("video/")) return "video";
    if (mime.startsWith("audio/")) return "audio";
    return detectByExtension(file.name);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (!file) {
      setSelectedFile(undefined);
      return;
    }
    if (!detectType(file)) {
      setSelectedFile(undefined);
      setError(t("DAILY_ACTIVITY.UPLOAD_HINT"));
      return;
    }
    setSelectedFile(file);
    setError("");
  };

  const uploadFile = async (file: File) => {
    setLoading(true);
    try {
      const detected = detectType(file);
      const res = await uploadFileToS3(file, (value) => setProgress(value));
      const thumbnail =
        detected === "audio"
          ? await uploadAudioPresetThumbnail(AUDIO_PRESETS[0].id, uploadFileToS3)
          : undefined;
      if (res.file && detected) {
        onUpload({
          resourcePath: res.file,
          resourceSize: file.size,
          resourceName: file.name,
          resourceType: detected,
          url: res.url,
          ...(thumbnail?.file
            ? {
                thumbnailPath: thumbnail.file,
                thumbnailName: `${AUDIO_PRESETS[0].id}.png`,
                thumbnailUrl: thumbnail.url,
              }
            : {}),
        });
      }
    } catch (e) {
      setError((e as Error)?.message || t("DAILY_ACTIVITY.UPLOAD_HINT"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedFile) {
      uploadFile(selectedFile);
    }
  }, [selectedFile]);

  return (
    <>
      <div className={styles.dotBox}>
        <Grid className={clsx(styles.uploadBox, error && styles.uploadBoxErr)}>
          <input
            type="file"
            accept={ACCEPT_ATTR}
            ref={docRef}
            className={styles.hiddenInput}
            onChange={handleFileChange}
          />

          <Grid
            onClick={() => docClick()}
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <Grid item className={styles.labelWrap}>
              <img className={styles.iconImage} src={uploadBtn} alt="upload" />
              <Typography noWrap className={styles.uploadLabel}>
                {t("DAILY_ACTIVITY.UPLOAD_FILE")}
              </Typography>
            </Grid>
            <Typography noWrap className={styles.caption}>
              {t("DAILY_ACTIVITY.UPLOAD_HINT")}
            </Typography>
          </Grid>
        </Grid>
        {isLoading ? (
          <LinearProgress
            className={styles.progress}
            variant="determinate"
            value={progress}
          />
        ) : null}
      </div>
      {error ? <FormHelperText error>{error}</FormHelperText> : null}
    </>
  );
};
